import axios, { wrappedAxiosCall } from 'base/APIClient';
import { doQuery } from 'base/GraphQLClient';
import { APP_MAIN, PROMOTIONS } from 'base/Url';
import { apiDateFormat } from 'constants';
import {
  getFormattedDate,
  getFormattedTime,
  groupDates,
} from 'utilities/DateUtils';
import { showMessage } from 'utilities/Utils';
import {
  analyticsDocumentQuery,
  analyticsHistogramQuery,
} from './PromotionQuery';
import { dateTimeFormat } from './Promotions.constants';

export const updateLoggedInConfigValue = (hotelCode, data) => {
  const url = APP_MAIN.V2_HOTEL(hotelCode);
  return wrappedAxiosCall({ method: 'put', url, data });
};

export const createPromotions = data => {
  const url = PROMOTIONS.CREATE_MULTIPLE_HOTEL_OFFERS;
  return new Promise((resolve, reject) => {
    axios
      .post(url, data, { useLoader: true })
      .then(({ data: dataArr }) => {
        dataArr = dataArr?.[0] ?? {};
        if (!dataArr.success) {
          showMessage({
            type: 'error',
            show: true,
            message:
              dataArr || Object.values(dataArr?.message || {})?.join('\n'),
          });
        } else {
          resolve({});
        }
      })
      .catch(err => {
        reject(err);
      });
  });
};

export const modifyPromotions = data => {
  const url = PROMOTIONS.UPDATE_HOTEL_OFFER;
  return wrappedAxiosCall({ method: 'post', url, data });
};

export const getPromotionPayload = (
  hotelCode,
  promotionType,
  formValues,
  isEditFlow,
) => {
  const {
    bookablePeriod,
    discountForNonLogin,
    extraDiscountForLogin,
    promotionName,
    checkinStartDate,
    checkinEndDate,
    bookingStartDate,
    bookingEndDate,
    blackoutDate,
    allRoomAndRp,
    selectedRoomAndRp,
    checkinNoEndDate,
    bookingNoEndDate,
    promotionBasedOn,

    //for modify
    offerCode,
    parentGroupId,
  } = formValues;

  const payload = {
    apply_on_linked_rp: true,
    checkinweekday: ['0', '1', '2', '3', '4', '5', '6'],
    offercondition: 'all',
    offercategory: promotionType,
    offer_value_list: [
      {
        channel: 'all',
        isactive: 1,
        offer_basis: 'discount',
        offer_type: 'percentage',
        offer_value: discountForNonLogin,
        segment: 'b2c',
      },
      {
        channel: 'all',
        isactive: 1,
        offer_basis: 'discount',
        offer_type: 'percentage',
        offer_value: discountForNonLogin + extraDiscountForLogin,
        segment: 'loggedin',
      },
    ],
    isactive: true,
    nonrefundable: false,
    pah_applicable: true,
    offer_name: promotionName?.trim(),
    checkindatestart: getFormattedTime(checkinStartDate, apiDateFormat),
    bookingdatestart:
      getFormattedTime(bookingStartDate, apiDateFormat) + dateTimeFormat,
    checkoutdateend:
      checkinNoEndDate && checkinEndDate
        ? getFormattedTime(checkinEndDate, apiDateFormat)
        : '2099-12-31',
    bookingdateend:
      bookingNoEndDate && bookingEndDate
        ? getFormattedTime(bookingEndDate, apiDateFormat) + dateTimeFormat
        : `2099-12-31${dateTimeFormat}`,
    earlybirdmin: promotionType === 'earlybird' ? +bookablePeriod : 0,
    earlybirdmax: promotionType === 'earlybird' ? 550 : +bookablePeriod,
    checkinblackoutdates: '',
    relatedtolist: [],
  };

  if (promotionType === 'earlybird' && promotionBasedOn === 'checkin') {
    payload.bookingdatestart =
      getFormattedTime(checkinStartDate, apiDateFormat) + dateTimeFormat;
    payload.bookingdateend = `2099-12-31${dateTimeFormat}`;
  }

  if (
    promotionType === 'lastminute' &&
    promotionBasedOn !== 'booking&checkin'
  ) {
    payload.checkindatestart = getFormattedTime(
      bookingStartDate,
      apiDateFormat,
    );
    payload.checkoutdateend = '2099-12-31';
  }

  if (isEditFlow) {
    payload.checkindatestart = payload.checkindatestart + dateTimeFormat;
    payload.checkoutdateend = payload.checkoutdateend + dateTimeFormat;
  }

  if (blackoutDate) {
    const allDates = groupDates(blackoutDate, true);
    const modifiedDates = allDates.map(
      dateArr =>
        `${getFormattedDate(dateArr[0], apiDateFormat)}:${getFormattedDate(
          dateArr[dateArr.length - 1],
          apiDateFormat,
        )}`,
    );
    payload.checkinblackoutdates = modifiedDates.join(',');
  }
  if (allRoomAndRp) {
    payload.relatedtolist = [
      {
        relatedto: 'hotel',
        relatedcode: hotelCode,
      },
    ];
  } else {
    Object.keys(selectedRoomAndRp).forEach(room => {
      const tempList = [];
      const currentRoomRpObj = selectedRoomAndRp[room];
      let isRoomChecked = true;
      Object.keys(currentRoomRpObj).forEach(rp => {
        if (currentRoomRpObj[rp]) {
          tempList.push({
            relatedto: 'Rateplan',
            relatedcode: rp,
          });
        } else {
          isRoomChecked = false;
        }
      });

      if (isRoomChecked) {
        payload.relatedtolist = [
          ...payload.relatedtolist,
          {
            relatedto: 'Room',
            relatedcode: room,
          },
        ];
      } else {
        payload.relatedtolist = [...payload.relatedtolist, ...tempList];
      }
    });
  }

  if (isEditFlow) {
    payload.offercode = offerCode;
    payload.parent_group_id = parentGroupId;
  }

  return payload;
};

export const getAnalyticsPromotion = (
  promotionType,
  locusCityCode,
  hotelCityId,
) => {
  const payload = {
    rawEndpointRequestData: {
      analyticsHistogram: {
        modelType: 'modelType1',
        endPointId: 1,
        body: {
          pipeline: 'Promotions',
          include: {
            locusCityCode: locusCityCode,
            'hotel.ncityId': hotelCityId,
            offerType: 'percentage',
            'offerConditionData.offerCategory': promotionType,
          },
          KPI: [
            {
              offerValue: 'avg',
            },
          ],
        },
      },
    },
  };

  return new Promise(resolve => {
    doQuery(analyticsHistogramQuery, payload, { useLoader: false })
      .then(resp => {
        const response =
          resp.data.rawEndpoint.analyticsHistogram.modelType1.data;
        resolve(response);
      })
      .catch(error => {
        console.error('analyticsHistogramQuery', error);
      });
  });
};

export const getSimilarPromotion = (hotelCode, promotionType) => {
  const payload = {
    rawEndpointRequestData: {
      analyticsDocuments: {
        modelType: 'modelType1',
        endPointId: 1,
        body: {
          pipeline: 'Promotions',
          include: {
            'offerConditionData.bookingDateEnd': {
              gte: '2099-12-31 00:00:00',
            },
            'offerConditionData.offerCategory': promotionType,
            hotelCode: hotelCode,
          },
          sort: {
            createdOn: 'desc',
          },
          fields: ['offerConditionData.offerName'],
          offset: 0,
          size: 1,
        },
      },
    },
  };

  return new Promise((resolve, reject) => {
    doQuery(analyticsDocumentQuery, payload, { useLoader: false })
      .then(resp => {
        const response =
          resp.data.rawEndpoint.analyticsDocuments.modelType1.data;
        if (response.doc_count > 0) {
          resolve(response.docs[0]);
        } else {
          reject(null);
        }
      })
      .catch(error => {
        console.error('analyticsDocumentQuery', error);
      });
  });
};
